import { storeConfig } from '@/shared/storeConfig';
import { useAuthStore } from '@/stores/auth';

type TournamentUpdateCallback = ((data: TTournament[]) => void) & {
  id?: string;
};

export const useSmarticoTournaments = defineStore(
  'smarticoTournaments',
  () => {
    const { debugLog, gamificationExclusiveTournaments } = useSmarticoConfigs();

    const tournaments = ref<TTournament[]>([]);
    const isLoading = ref<boolean>(false);
    const isCallbackRegistered = ref<boolean>(false);
    const customUpdateCallbacks = ref<Array<TournamentUpdateCallback>>([]);
    // In-memory cache of tournament details keyed by instance_id. Survives
    // SPA navigation between pages, cleared on full reload (F5).
    const tournamentInfoCache = ref<Map<number, TTournamentDetailed>>(
      new Map(),
    );

    function getCachedTournamentInfo(
      instanceId: number,
    ): TTournamentDetailed | undefined {
      return tournamentInfoCache.value.get(instanceId);
    }

    function handleTournamentsUpdate(updatedTournaments: TTournament[]) {
      if (!updatedTournaments || !Array.isArray(updatedTournaments)) {
        debugLog('⚠️ onUpdate callback received invalid data for tournaments');

        return;
      }

      debugLog(
        `🔄 Tournaments updated via callback onUpdate: ${updatedTournaments.length} tournaments`,
      );
      tournaments.value = updatedTournaments;

      // Execute custom callbacks registered by components
      if (customUpdateCallbacks.value.length > 0) {
        customUpdateCallbacks.value.forEach((callback) => {
          try {
            callback(updatedTournaments);
          } catch (err: any) {
            debugLog(
              `⭕️ ~ custom callback execution error ~ ${err.toString()}`,
            );
          }
        });
      }
    }

    function registerUpdateCallback(api: any) {
      if (isCallbackRegistered.value) {
        return;
      }

      try {
        if (typeof api?.registerTournamentsUpdateCallback === 'function') {
          api.registerTournamentsUpdateCallback(handleTournamentsUpdate);
          isCallbackRegistered.value = true;
          debugLog('✅ Tournaments update callback registered successfully');
        }
      } catch (err: any) {
        debugLog(`⭕️ ~ registerTournamentsUpdateCallback ~ ${err.toString()}`);
      }
    }

    const exclusiveTournaments = computed(() => {
      if (!gamificationExclusiveTournaments.length) {
        return [];
      }

      return tournaments.value.filter((tournament) =>
        gamificationExclusiveTournaments.includes(
          String(tournament.tournament_id),
        ),
      );
    });

    const allTournaments = computed(() => {
      if (!gamificationExclusiveTournaments.length) {
        return tournaments.value;
      }

      return tournaments.value.filter(
        (tournament) =>
          !gamificationExclusiveTournaments.includes(
            String(tournament.tournament_id),
          ),
      );
    });

    async function loadTournaments(): Promise<void> {
      try {
        isLoading.value = true;
        tournaments.value = [];
        isCallbackRegistered.value = false;

        const { isAuthenticated } = useAuthStore();

        if (
          !window._smartico ||
          (!window._smartico.api && !window._smartico.vapi)
        ) {
          console.error('window._smartico or its API/vAPI is not available.');
          debugLog(
            '⭕️ ~ getTournamentsList ~ window._smartico API/vAPI not available',
          );
          isLoading.value = false;

          return;
        }

        const api = isAuthenticated
          ? window._smartico.api
          : window._smartico.vapi('EN');

        if (!api || typeof api.getTournamentsList !== 'function') {
          console.error(
            'Smartico API instance or getTournamentsList function is not available.',
          );
          debugLog(
            '⭕️ ~ getTournamentsList ~ Smartico API instance or function not available',
          );
          isLoading.value = false;

          return;
        }

        registerUpdateCallback(api);

        const tournamentsPromise = api.getTournamentsList({
          onUpdate: handleTournamentsUpdate,
        });

        const initialTournaments = await tournamentsPromise;

        tournaments.value = initialTournaments;

        debugLog(`📊 Loaded ${tournaments.value.length} tournaments initially`);
      } catch (err: any) {
        debugLog(`⭕️ ~ getTournamentsList ~ ${err.toString()}`);
        console.error('Error loading tournaments:', err); // Adicionar log de erro
      } finally {
        isLoading.value = false;
      }
    }

    async function loadTournamentInfo(tournamentInstanceId: number) {
      const cached = tournamentInfoCache.value.get(tournamentInstanceId);

      if (cached) return cached;

      try {
        const { isAuthenticated } = useAuthStore();
        const api = isAuthenticated
          ? window._smartico.api
          : window._smartico.vapi('EN');

        const tournamentDetailed =
          await api.getTournamentInstanceInfo(tournamentInstanceId);

        if (tournamentDetailed) {
          tournamentInfoCache.value.set(
            tournamentInstanceId,
            tournamentDetailed as TTournamentDetailed,
          );
        }

        return tournamentDetailed as TTournamentDetailed;
      } catch (err: any) {
        debugLog(`⭕️ ~ getTournamentInstanceInfo ~ ${err.toString()}`);
      }
    }

    function registerCustomUpdateCallback(callback: TournamentUpdateCallback) {
      if (typeof callback === 'function') {
        const exists = customUpdateCallbacks.value.some(
          (cb) => cb.id === callback.id,
        );

        if (!exists) {
          customUpdateCallbacks.value.push(callback);
          debugLog('✅ Custom tournaments update callback registered');
        }
      }
    }

    function unregisterCustomUpdateCallback(
      callback: TournamentUpdateCallback,
    ) {
      const index = customUpdateCallbacks.value.findIndex(
        (cb) => cb.id === callback.id,
      );

      if (index !== -1) {
        customUpdateCallbacks.value.splice(index, 1);
        debugLog('✅ Custom tournaments update callback unregistered');
      }
    }

    async function enterInTournament(tournamentInstanceId: number) {
      try {
        await window._smartico.api.registerInTournament(tournamentInstanceId);
        // Invalidate cached details so registration count / status refresh.
        tournamentInfoCache.value.delete(tournamentInstanceId);
        await loadTournamentInfo(tournamentInstanceId);
        await loadTournaments();
      } catch (err: any) {
        debugLog(`⭕️ ~ registerInTournament ~ ${err.toString()}`);
      }
    }

    return {
      tournaments,
      exclusiveTournaments,
      allTournaments,
      isLoading,
      loadTournaments,
      loadTournamentInfo,
      getCachedTournamentInfo,
      registerCustomUpdateCallback,
      unregisterCustomUpdateCallback,
      enterInTournament,
    };
  },

  storeConfig,
);
